/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { ListViewportConfig, ListScrollPosition } from '../List.types';
import type { ScrollDetail, ViewportInfo } from '../List';

import { findElementByKey } from '#utils/PRIVATE_collectionUtils';
import { getRowKey } from '#PRIVATE_TableCommon/utils/DomUtils';
import {
  getScrollbarHeight,
  getVerticalOverflowDiff,
  isHTMLScroller
} from '#PRIVATE_TableCommon/utils/ScrollUtils';
import { getElementFromCell, getRowKeyForRowIndex, LIST_ROW_SELECTOR } from './ListDomUtils';

/**
 * Helper method to get the top scroller offset of the List
 */
const _getScrollerTopOffset = (viewportConfig: ListViewportConfig) => {
  return viewportConfig.scrollerOffsetTop ?? 0;
};

/**
 * Helper method to get the bottom scroller offset of the List
 */
const _getScrollerBottomOffset = (viewportConfig: ListViewportConfig) => {
  return viewportConfig.scrollerOffsetBottom ?? 0;
};

/**
 * Helper method to return the top position of the scroller element.
 */
export const getScrollerTop = (
  scrollerElement: HTMLElement,
  viewportConfig: ListViewportConfig
) => {
  return isHTMLScroller(scrollerElement)
    ? _getScrollerTopOffset(viewportConfig)
    : _getScrollerTopOffset(viewportConfig) + scrollerElement.getBoundingClientRect().top;
};

/**
 * Helper method to return the bottom position of the scroller element.
 */
export const getScrollerBottom = (
  scrollerElement: HTMLElement,
  viewportConfig: ListViewportConfig
) => {
  const bottomOffset = _getScrollerBottomOffset(viewportConfig);
  if (isHTMLScroller(scrollerElement)) {
    return scrollerElement.clientHeight - bottomOffset;
  }
  const scrollbarHeight = getScrollbarHeight(scrollerElement);
  return scrollerElement.getBoundingClientRect().bottom - (bottomOffset + scrollbarHeight);
};

/**
 * A helper function to make sure specified elem is visible in the specified container
 */
const _scrollRowIntoView = (
  rowElement: HTMLElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  location?: 'inView' | 'top' | 'bottom'
) => {
  const verticalOverflowDiff = getVerticalOverflowDiff(
    rowElement,
    getScrollerTop(scrollerElement, viewportInfo.viewportConfig),
    getScrollerBottom(scrollerElement, viewportInfo.viewportConfig)
  );
  const topOverflowDiff = verticalOverflowDiff.top;
  const hasTopOverflow = topOverflowDiff > 0;
  const bottomOverflowDiff = verticalOverflowDiff.bottom;
  const hasBottomOverflow = bottomOverflowDiff > 0;

  // don't adjust scroll position if row has overflow in both directions
  if (hasTopOverflow && hasBottomOverflow) {
    return;
  }
  if (location == null || location === 'inView') {
    // if row fits fully in viewport, scroll overflow side into view
    // otherwise, scroll the shortest amount to fill viewport with row
    if (hasBottomOverflow) {
      if (Math.abs(topOverflowDiff) > Math.abs(bottomOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      }
    } else if (hasTopOverflow) {
      if (Math.abs(bottomOverflowDiff) > Math.abs(topOverflowDiff)) {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop - topOverflowDiff,
          false
        );
      } else {
        scrollerElement.scrollTop = _roundScrollValue(
          scrollerElement.scrollTop + bottomOverflowDiff,
          true
        );
      }
    }
  } else if (location === 'top') {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop - topOverflowDiff,
      false
    );
  } else {
    scrollerElement.scrollTop = _roundScrollValue(
      scrollerElement.scrollTop + bottomOverflowDiff,
      true
    );
  }
};

/**
 * Helper method to round a desired scrollTop or scrollLeft value to the nearest valid value
 * depending on the client browser.
 */
const _roundScrollValue = (value: number, isOver: boolean) => {
  // use Math.floor() and Math.ceil() to ensure we round to nearest containing valid
  // pixel value in either direction as valid scrollTop values are only integers
  return isOver ? Math.ceil(value) : Math.floor(value);
};

/**
 * Helper to set the vertical scroll position on the List.
 */
export function setScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string,
  scrollPosition?: ListScrollPosition<K>
) {
  const y = _getScrollTopFromScrollPosition(
    rootElement,
    scrollerElement,
    viewportInfo,
    listId,
    scrollPosition
  );
  scrollerElement.scrollTop = y ?? 0;
}

/**
 * Helper to determine the 'y' value of a given scroll position.
 */
function _getScrollTopFromScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string,
  scrollPosition?: ListScrollPosition<K>
) {
  if (scrollPosition != null) {
    let rowKey;
    let useRow = false;
    if (scrollPosition.key != null) {
      // rowKey takes precedence over everything else
      useRow = true;
      rowKey = scrollPosition.key;
    } else if (scrollPosition.offsetY != null && scrollPosition.y == null) {
      // offsetY can be used on its own if it is the only vertical aspect provided
      useRow = true;
      rowKey = getScrollPosition(rootElement, scrollerElement, viewportInfo, listId).key;
    }
    if (useRow) {
      if (rowKey != null) {
        const scrollerTop = getScrollerTop(scrollerElement, viewportInfo.viewportConfig);
        const offsetY = scrollPosition.offsetY ?? 0;
        const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
        for (let i = 0; i < rowElements.length; i++) {
          if (rowKey === getRowKey(rowElements[i] as HTMLElement)) {
            const rowTop = rowElements[i].getBoundingClientRect().top;
            return scrollerElement.scrollTop + rowTop - scrollerTop + offsetY;
          }
        }
      }
    } else if (scrollPosition.y != null) {
      return scrollPosition.y;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}

/**
 * Helper to get the logical scroll position for the given scrollTop value. If no scrollTop value is given,
 * it will use the current scrollTop value of the List.
 */
export function getScrollPosition<K>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string
) {
  // ensure scroll position y value is initialized
  const scrollPosition: ListScrollPosition<K> = {
    y: Math.abs(scrollerElement.scrollTop),
    key: undefined,
    offsetY: undefined
  };

  const scrollerTop = getScrollerTop(scrollerElement, viewportInfo.viewportConfig);
  const rowElements = rootElement.querySelectorAll(`[${LIST_ROW_SELECTOR}='${listId}']`);
  for (let i = 0; i < rowElements.length; i++) {
    const rowRect = rowElements[i].getBoundingClientRect();
    if (rowRect.bottom > scrollerTop || i === rowElements.length - 1) {
      scrollPosition.key = getRowKeyForRowIndex<K>(rootElement, listId, i)!;
      scrollPosition.offsetY = scrollerTop - rowRect.top;
      break;
    }
  }
  return scrollPosition;
}

export function applyScrollDetail<K extends string | number>(
  rootElement: HTMLDivElement,
  scrollerElement: HTMLElement,
  viewportInfo: ViewportInfo,
  listId: string,
  scrollDetail: ScrollDetail<K>
) {
  if (scrollDetail.cell.type === 'data') {
    const rowElement = findElementByKey(
      rootElement,
      scrollDetail.cell.rowKey,
      `[${LIST_ROW_SELECTOR}='${listId}']`
    ) as HTMLElement;
    if (rowElement != null && scrollDetail.locationY != null) {
      _scrollRowIntoView(rowElement, scrollerElement, viewportInfo, scrollDetail.locationY);
    }
  } else if (scrollDetail.cell.type === 'loadMore' || scrollDetail.cell.type === 'loading') {
    const cellElement = getElementFromCell(rootElement, listId, scrollDetail.cell);
    if (cellElement != null && scrollDetail.locationY != null) {
      _scrollRowIntoView(
        cellElement,
        scrollerElement,
        viewportInfo,
        scrollDetail.cell.type === 'loading' && scrollDetail.locationY === 'inView'
          ? 'top'
          : scrollDetail.locationY
      );
    }
  }
}

/**
 * Helper function to reset the scroll position of the scroller element
 */
export const resetScrollPosition = (scrollerElement: HTMLElement) => {
  scrollerElement.scrollTop = 0;
};

/**
 * Helper function to get the viewport config.
 */
export const getViewportConfig = (
  scrollerRef: RefObject<HTMLElement>,
  config?: ListViewportConfig
) => {
  return config ?? { scrollerRef };
};
